'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Upload, X, FileText, Image, File, CheckCircle } from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

export interface FileUploadProps {
  /** Callback when files are added (via drop or browse) */
  onUpload: (files: File[]) => void;
  /** Accepted MIME types (e.g. "image/*", "application/pdf") */
  accept?: string;
  /** Allow multiple files */
  multiple?: boolean;
  /** Maximum number of files */
  maxFiles?: number;
  /** Maximum file size in MB */
  maxSizeMB?: number;
  /** Disable the component */
  disabled?: boolean;
  /** Additional className for the container */
  className?: string;
}

interface FilePreview {
  id: string;
  file: File;
  previewUrl: string | null;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
}

// ============================================================================
// HELPERS
// ============================================================================

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function isImageType(type: string): boolean {
  return type.startsWith('image/');
}

function getFileIcon(type: string) {
  if (isImageType(type)) return <Image className="h-5 w-5 text-blue-400" />;
  if (type === 'application/pdf') return <FileText className="h-5 w-5 text-red-400" />;
  return <File className="h-5 w-5 text-gray-400" />;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function FileUpload({
  onUpload,
  accept,
  multiple = false,
  maxFiles = 10,
  maxSizeMB = 10,
  disabled = false,
  className,
}: FileUploadProps) {
  const [files, setFiles] = useState<FilePreview[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCountRef = useRef(0);

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      files.forEach((f) => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------------------
  // Validation + processing
  // -------------------------------------------------------------------------

  const processFiles = useCallback(
    (incoming: FileList | File[]) => {
      setError(null);
      const arr = Array.from(incoming);

      // Check max files
      const totalAfter = files.length + arr.length;
      if (totalAfter > maxFiles) {
        setError(`Maximum ${maxFiles} file${maxFiles === 1 ? '' : 's'} allowed`);
        return;
      }

      // Check sizes
      const maxBytes = maxSizeMB * 1024 * 1024;
      const oversized = arr.find((f) => f.size > maxBytes);
      if (oversized) {
        setError(`"${oversized.name}" exceeds the ${maxSizeMB} MB limit`);
        return;
      }

      // Build previews
      const newPreviews: FilePreview[] = arr.map((file) => ({
        id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        previewUrl: isImageType(file.type) ? URL.createObjectURL(file) : null,
        progress: 0,
        status: 'pending' as const,
      }));

      setFiles((prev) => [...prev, ...newPreviews]);

      // Simulate upload progress for each file, then fire onUpload
      newPreviews.forEach((fp) => {
        simulateProgress(fp.id);
      });

      // Notify parent with the raw File objects
      onUpload(arr);
    },
    [files.length, maxFiles, maxSizeMB, onUpload]
  );

  const simulateProgress = useCallback((fileId: string) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 30 + 10;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileId ? { ...f, progress: 100, status: 'done' as const } : f
          )
        );
      } else {
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileId ? { ...f, progress: Math.min(progress, 95), status: 'uploading' as const } : f
          )
        );
      }
    }, 200);
  }, []);

  // -------------------------------------------------------------------------
  // Drag handlers
  // -------------------------------------------------------------------------

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      dragCountRef.current++;
      if (dragCountRef.current === 1) setIsDragging(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      dragCountRef.current--;
      if (dragCountRef.current === 0) setIsDragging(false);
    },
    [disabled]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
    },
    [disabled]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      dragCountRef.current = 0;
      setIsDragging(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        processFiles(e.dataTransfer.files);
      }
    },
    [disabled, processFiles]
  );

  // -------------------------------------------------------------------------
  // Click handler
  // -------------------------------------------------------------------------

  const handleBrowse = useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(e.target.files);
      }
      // Reset input so re-selecting the same file works
      e.target.value = '';
    },
    [processFiles]
  );

  // -------------------------------------------------------------------------
  // Remove a file
  // -------------------------------------------------------------------------

  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === fileId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((f) => f.id !== fileId);
    });
  }, []);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className={cn('space-y-3', className)}>
      {/* Drop zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleBrowse}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleBrowse();
        }}
        className={cn(
          'relative flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-8 transition-all cursor-pointer',
          'bg-secondary-800/30 border-secondary-600 hover:border-primary-500/60 hover:bg-secondary-800/50',
          isDragging && 'border-primary-400 bg-primary-500/10 scale-[1.01]',
          disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary-900'
        )}
      >
        <Upload
          className={cn(
            'h-8 w-8 transition-colors',
            isDragging ? 'text-primary-400' : 'text-secondary-400'
          )}
        />
        <div className="text-center">
          <p className="text-sm font-medium text-secondary-200">
            {isDragging ? 'Drop files here' : 'Drag & drop files here'}
          </p>
          <p className="mt-1 text-xs text-secondary-500">
            or <span className="text-primary-400 underline">browse</span> to choose
          </p>
        </div>
        {accept && (
          <p className="text-[10px] text-secondary-600 mt-1">
            Accepted: {accept}
          </p>
        )}
        <p className="text-[10px] text-secondary-600">
          Max {maxSizeMB} MB{multiple ? ` | Up to ${maxFiles} files` : ''}
        </p>
      </div>

      {/* Hidden input */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        className="hidden"
      />

      {/* Error message */}
      {error && (
        <p className="text-xs text-danger-400 px-1">{error}</p>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((fp) => (
            <div
              key={fp.id}
              className="flex items-center gap-3 rounded-xl bg-secondary-800/60 border border-secondary-700 p-3"
            >
              {/* Thumbnail or icon */}
              {fp.previewUrl ? (
                <img
                  src={fp.previewUrl}
                  alt={fp.file.name}
                  className="h-10 w-10 rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-700/50 flex-shrink-0">
                  {getFileIcon(fp.file.type)}
                </div>
              )}

              {/* Name + size + progress */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-secondary-100 truncate">{fp.file.name}</p>
                <p className="text-xs text-secondary-500">{formatFileSize(fp.file.size)}</p>

                {/* Progress bar */}
                {fp.status === 'uploading' && (
                  <div className="mt-1.5 h-1 w-full rounded-full bg-secondary-700 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary-500 transition-all duration-200"
                      style={{ width: `${fp.progress}%` }}
                    />
                  </div>
                )}
              </div>

              {/* Status / remove */}
              {fp.status === 'done' ? (
                <CheckCircle className="h-4 w-4 text-success-500 flex-shrink-0" />
              ) : null}

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(fp.id);
                }}
                className="p-1 rounded-lg text-secondary-500 hover:text-secondary-200 hover:bg-secondary-700 transition-colors flex-shrink-0"
                aria-label={`Remove ${fp.file.name}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
